// src/routes/statistics/report/services/dailyGenerationService.js

import { eachDayOfInterval, format } from 'date-fns';
import config from '../../../../config';
import { parseEvSensorData } from '../../../../utils/parseEvSensorData';
import { generateColor } from '../utils/chartColors';

export const fetchAndBuildDailyGeneration = async ({
  start,
  end,
  mode,
  ivList,
  Ev_list,
  totalCapacity,
}) => {
  const startDateStr = format(start, 'yyyy-MM-dd');
  const endDateStr = format(end, 'yyyy-MM-dd');
  const body = JSON.stringify({
    start_date: startDateStr,
    end_date: endDateStr,
  });

  const [ivRes, envRes] = await Promise.all([
    fetch(`${config.backendUrl}/get_day_Iv_data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    }),
    fetch(`${config.backendUrl}/get_hourly_Ev_mrt_data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    }),
  ]);

  let ivRows = [];
  if (ivRes.ok) {
    try {
      const ivJson = await ivRes.json();
      ivRows = Array.isArray(ivJson?.data)
        ? ivJson.data
        : Array.isArray(ivJson)
          ? ivJson
          : [];
    } catch (e) {
      console.warn('인버터 응답 JSON 파싱 실패:', e);
    }
  } else {
    console.warn(`인버터 요청 실패: ${ivRes.status} — 빈 데이터로 조회합니다.`);
  }

  let envJson = null;
  if (envRes.ok) {
    try {
      envJson = await envRes.json();
    } catch (e) {
      console.warn('환경센서 응답 JSON 파싱 실패:', e);
    }
  } else {
    console.warn(`환경센서 요청 실패: ${envRes.status} — 빈 데이터로 조회합니다.`);
  }

  const envRows =
    envJson?.status === 'error'
      ? []
      : Array.isArray(envJson?.data)
        ? envJson.data
        : Array.isArray(envJson)
          ? envJson
          : [];

  const safeIvList = {
    seq: ivList?.seq || [],
    iv_name: ivList?.iv_name || [],
    iv_capa: ivList?.iv_capa || [],
  };

  const allDates = eachDayOfInterval({
    start: new Date(start),
    end: new Date(end),
  }).map((d) => format(d, 'yyyy-MM-dd'));

  let parsedEnv = {
    horizontalIrradiance: allDates.map(() => null),
    tiltedIrradiance: allDates.map(() => null),
    surfaceTemp: allDates.map(() => null),
    outdoorTemp: allDates.map(() => null),
  };

  try {
    const result = parseEvSensorData({ data: envRows }, allDates, Ev_list);

    parsedEnv = {
      horizontalIrradiance: Array.isArray(result?.horizontalIrradiance)
        ? result.horizontalIrradiance
        : allDates.map(() => null),
      tiltedIrradiance: Array.isArray(result?.tiltedIrradiance)
        ? result.tiltedIrradiance
        : allDates.map(() => null),
      surfaceTemp: Array.isArray(result?.surfaceTemp)
        ? result.surfaceTemp
        : allDates.map(() => null),
      outdoorTemp: Array.isArray(result?.outdoorTemp)
        ? result.outdoorTemp
        : allDates.map(() => null),
    };
  } catch (e) {
    console.warn('환경센서 데이터 파싱 실패:', e);
  }

  const {
    horizontalIrradiance,
    tiltedIrradiance,
    surfaceTemp,
    outdoorTemp,
  } = parsedEnv;

  let nextDisplayedData = null;
  let formattedCsvData = [];

  if (mode === 'total') {
    const totalData = allDates.map((date) => {
      return (safeIvList.seq || []).reduce((sum, seq) => {
        const entry = ivRows.find(
          (item) =>
            item.seq === seq &&
            String(item.created_at || '').split(' ')[0] === date
        );
        return sum + (Number(entry?.Iv_Add_power) || 0);
      }, 0);
    });

    nextDisplayedData = {
      labels: allDates,
      datasets: [
        {
          label: '전체 합계',
          data: totalData,
          backgroundColor: 'rgba(255, 99, 132, 0.6)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
        {
          label: '수평 일사량',
          data: horizontalIrradiance,
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
          yAxisID: 'y1',
        },
        {
          label: '경사 일사량',
          data: tiltedIrradiance,
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
          yAxisID: 'y1',
        },
        {
          label: '외기 온도',
          data: outdoorTemp,
          borderColor: 'rgba(255, 206, 86, 1)',
          borderWidth: 1,
          yAxisID: 'y1',
        },
        {
          label: '표면 온도',
          data: surfaceTemp,
          borderColor: 'rgba(153, 102, 255, 1)',
          borderWidth: 1,
          yAxisID: 'y1',
        },
      ],
    };

    formattedCsvData = [
      ['날짜', '전체 합계 (kWh)', '발전시간 (h)', '수평 일사량 (W/m²)', '경사 일사량 (W/m²)', '모듈 온도 (℃)', '외기 온도 (℃)'],
      ...allDates.map((date, idx) => [
        date,
        ((totalData[idx] || 0) / 1000).toFixed(2) + ' kWh',
        (totalCapacity > 0 ? (totalData[idx] || 0) / totalCapacity / 1000 : 0).toFixed(2) + ' h',
        horizontalIrradiance[idx] != null ? Number(horizontalIrradiance[idx]).toFixed(2) + ' W/m²' : '',
        tiltedIrradiance[idx] != null ? Number(tiltedIrradiance[idx]).toFixed(2) + ' W/m²' : '',
        surfaceTemp[idx] != null ? Number(surfaceTemp[idx]).toFixed(1) + ' ℃' : '',
        outdoorTemp[idx] != null ? Number(outdoorTemp[idx]).toFixed(1) + ' ℃' : '',
      ]),
    ];
  } else {
    const totalInverters = (safeIvList.seq || []).length;

    const inverterDatasets = (safeIvList.seq || []).map((seq, index) => {
      const ivData = ivRows.filter((item) => item.seq === seq);

      const rowData = allDates.map((label) => {
        const entry = ivData.find(
          (item) => String(item.created_at || '').split(' ')[0] === label
        );
        return Number(entry?.Iv_Add_power) || 0;
      });

      return {
        label: (safeIvList.iv_name || [])[index] || `인버터 ${index + 1}`,
        data: rowData,
        backgroundColor: generateColor(index, totalInverters || 1),
        borderColor: generateColor(index, totalInverters || 1),
        borderWidth: 2,
        fill: false,
        yAxisID: 'y',
      };
    });

    const envSensorDatasets = [
      {
        label: '수평 일사량',
        data: horizontalIrradiance,
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.3)',
        borderWidth: 1,
        yAxisID: 'y1',
      },
      {
        label: '경사 일사량',
        data: tiltedIrradiance,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.3)',
        borderWidth: 1,
        yAxisID: 'y1',
      },
      {
        label: '외기 온도',
        data: outdoorTemp,
        borderColor: 'rgba(255, 206, 86, 1)',
        backgroundColor: 'rgba(255, 206, 86, 0.3)',
        borderWidth: 1,
        yAxisID: 'y1',
      },
      {
        label: '표면 온도',
        data: surfaceTemp,
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.3)',
        borderWidth: 1,
        yAxisID: 'y1',
      },
    ];

    nextDisplayedData = {
      labels: allDates,
      datasets: [...inverterDatasets, ...envSensorDatasets],
    };

    formattedCsvData = [
      [
        '날짜',
        ...(safeIvList.iv_name || []).map((name) => `${name} (kWh)`),
        '발전시간 (h)',
        '합계 (kWh)',
        '수평 일사량 (W/m²)',
        '경사 일사량 (W/m²)',
        '모듈 온도 (℃)',
        '외기 온도 (℃)',
      ],
      ...allDates.map((date, idx) => {
        const rowSum = inverterDatasets.reduce(
          (sum, dataset) => sum + (Number(dataset.data[idx]) || 0),
          0
        );

        return [
          date,
          ...inverterDatasets.map((dataset) =>
            ((Number(dataset.data[idx]) || 0) / 1000).toFixed(2)
          ),
          (totalCapacity > 0 ? rowSum / totalCapacity / 1000 : 0).toFixed(2) + ' h',
          (rowSum / 1000).toFixed(2),
          horizontalIrradiance[idx] != null ? Number(horizontalIrradiance[idx]).toFixed(2) + ' W/m²' : '',
          tiltedIrradiance[idx] != null ? Number(tiltedIrradiance[idx]).toFixed(2) + ' W/m²' : '',
          surfaceTemp[idx] != null ? Number(surfaceTemp[idx]).toFixed(1) + ' ℃' : '',
          outdoorTemp[idx] != null ? Number(outdoorTemp[idx]).toFixed(1) + ' ℃' : '',
        ];
      }),
    ];
  }

  return {
    displayed: nextDisplayedData,
    csv: formattedCsvData,
  };
};